import { useMemo } from "react"
import PropTypes from "prop-types"
import { get, map } from "lodash"

import classNames from "classnames"
import { Outlet } from "react-router-dom"
import { AppBar } from "@mui/material"
import { DATA_STATUSES } from "helpers/data-statuses"
import Notifications from "modules/notifications"
import ServerNotifications from "modules/server-notifications"
import StaticAlert from "modules/static-alert"
import FactorProfile from "modules/factor-profile"
import SecurityProfile from "modules/security-profile"
import HelpMenu from "modules/help-menu"
import ThemeModeToggle from "components/theme-mode-toggle"
import Dialog from "modules/dialog"
import Modal from "components/modal"
import LogoHomeLink from "components/logo/home-link"
import { useIsDefinedRouterMatchProp, useLastRouterMatchProp } from "hooks/router"
import ResourceTooltipInfo from "components/resource-tooltip-info"
import LoadingWrapper from "components/loading-wrapper"
import NavRailShell from "connected-components/global-nav-rail/shell"
import { KellyLauncherButton } from "connected-components/agent-chat/components/kelly-launcher-button"

import "./style.scss"

export default function Home({
  header,
  account,
  security,
  model,
  urlModel,
  models,
  setModel,
  closeSecurityProfile,
  factorId,
  closeFactorProfile,
  setHeader,
  sessionExpired,
  status,
}) {
  const _HeaderElem = useLastRouterMatchProp("header")
  const _SubnavElem = useLastRouterMatchProp("subnav")
  const HeaderIcon = useLastRouterMatchProp("headerIcon")
  const headerTitle = useLastRouterMatchProp("headerTitle")
  const headerActions = useLastRouterMatchProp("headerActions")
  const noSideMenu = useLastRouterMatchProp("noSideMenu", false)

  const noModelSelector = useIsDefinedRouterMatchProp("noModelSelector")
  const modelFromUrl = useIsDefinedRouterMatchProp("modelFromUrl")
  // A child route can set `stateHeader: true` to opt out of an ancestor's route-declared header
  // and keep using the redux header path (e.g. smart-trade, which manages its own header via getHeaderConfig).
  const stateHeader = useIsDefinedRouterMatchProp("stateHeader")
  const HeaderElem = stateHeader ? null : _HeaderElem
  const SubnavElem = stateHeader ? null : _SubnavElem

  // The header prop is not accessible from the selectors
  const selectedModelId = get(header, "modelId") || (modelFromUrl ? urlModel : model)

  // Nothing clears `header` on navigation, so a route with its own header can inherit a stale subnav.
  const isHeaderFromState = !HeaderElem && !headerTitle

  const outletContext = useMemo(() => ({ setHeader, appHeaderKey: header.key }), [setHeader, header.key])

  // Gated with every other stale header field: `handleSetHeader` rebuilds the object from a fixed key
  // list, so a config that omits `noShadow` cleared it — a route-declared bar has no config to clear.
  return <div className={classNames("modules_home-layout", { "no-shadow": isHeaderFromState && header.noShadow })}>
    {HeaderElem && <HeaderElem account={account} />}
    {headerTitle && <AppBar position="sticky">
      <div className="header-app-bar">
        <div className="left">
          <LogoHomeLink account={account} />
          {HeaderIcon && <HeaderIcon />}
          <span className="title">{headerTitle}</span>
        </div>
        <div className="right">
          {headerActions && <div className="header-app-actions">
            {map(headerActions, (Action, index) =>
              <div key={`rightAction-${index}`} className="header-app-action">
                <Action />
              </div>,
            )}
          </div>}
          <ServerNotifications />
          <HelpMenu />
          <ThemeModeToggle inline />
          <KellyLauncherButton />
        </div>
      </div>
    </AppBar>}
    {isHeaderFromState && <AppBar elevation={header.noShadow ? 0 : 4} position="sticky">
      <div className="header-app-bar">
        <div className="left">
          <LogoHomeLink account={account} onClick={get(header, "actions.home")} />
          {header.icon && <header.icon />}
          {header.title && <span className="title">{header.title}</span>}
          {header.info && header.info.length > 0
            && <div className="info-tooltip">
              <ResourceTooltipInfo info={header.info} />
            </div>}
          {/* Trails the resource name, as the ⋮ does in the dashboard and the two nav bars. */}
          {header.menu}
        </div>
        <div className="center">
          {header.topnav && <span className="topnav">{header.topnav}</span>}
        </div>
        <div className="right">
          {header.actions && header.actions.rightActions && <div className="header-app-actions">
            {map(header.actions.rightActions, (action, index) =>
              <div key={`rightAction-${index}`} className="header-app-action">
                {action}
              </div>)}
          </div>}
          <ServerNotifications />
          {get(header, "actions.help") ? header.actions.help : <HelpMenu />}
          <ThemeModeToggle inline />
          <KellyLauncherButton />
        </div>
      </div>
    </AppBar>}
    <NavRailShell
      showRail={!noSideMenu}
      subnav={SubnavElem
        ? <div className="header-subnav"><SubnavElem /></div>
        : (isHeaderFromState && header.subnav
          ? <div className="header-subnav">{header.subnav}</div>
          : null)}
    >
      <StaticAlert />
      <LoadingWrapper status={[status]}>
        <Outlet context={outletContext} />
      </LoadingWrapper>
      <Notifications />
    </NavRailShell>
    <Dialog />
    {security && <Modal superModalOpened={sessionExpired} onClose={closeSecurityProfile} className="modules_security-profile" noButton>
      <SecurityProfile security={security} modelId={selectedModelId} models={models} setModel={!noModelSelector ? setModel : null} onClose={closeSecurityProfile} />
    </Modal>}
    {factorId && <Modal superModalOpened={sessionExpired} onClose={closeFactorProfile} className="modules_factor-profile" noButton>
      <FactorProfile factorId={factorId} modelId={selectedModelId} onClose={closeFactorProfile} />
    </Modal>}
  </div>
}

Home.propTypes = {
  model: PropTypes.string,
  // The model resolved from the URL, for routes that declare `modelFromUrl`.
  urlModel: PropTypes.string,
  setModel: PropTypes.func.isRequired,
  models: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
  security: PropTypes.shape({
    ticker: PropTypes.string,
    exchange: PropTypes.string,
  }),
  account: PropTypes.string.isRequired,
  closeSecurityProfile: PropTypes.func.isRequired,
  setHeader: PropTypes.func.isRequired,
  // Written wholesale by `setHeader` and never cleared on navigation, so any field can be stale.
  header: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.node,
    icon: PropTypes.elementType,
    menu: PropTypes.node,
    topnav: PropTypes.node,
    subnav: PropTypes.node,
    info: PropTypes.array,
    modelId: PropTypes.string,
    noShadow: PropTypes.bool,
    actions: PropTypes.shape({
      home: PropTypes.func,
      help: PropTypes.node,
      rightActions: PropTypes.array,
    }),
  }).isRequired,
  closeFactorProfile: PropTypes.func.isRequired,
  factorId: PropTypes.string,
  sessionExpired: PropTypes.bool,
  // The status the outlet is gated on.
  status: PropTypes.oneOf(DATA_STATUSES).isRequired,
}
